import { readPnl } from "@/features/positions/utils/pnl-display";
import { readDirection } from "@/features/positions/utils/direction-display";
import { MARK_MAX_AGE_SECONDS } from "@/features/positions/utils/mark-staleness";
import {
  useQueryClient,
  type QueryClient,
  type QueryFilters,
} from "@tanstack/react-query";
import { recordQuotePulse } from "@/features/market-session/quote-pulse";
import {
  calculatePositionPnL,
  getContractMultiplier,
  quoteRootFor,
} from "./pnl-calculator";
import type { Position } from "@/features/positions";
import { debugService } from "@/features/debug";

const quoteMissesLoggedThisSession = new Set<string>();

/**
 * Last live quote per verified dated contract, else futures root, so a REST
 * positions reconcile (which serves the DB's stored — frequently zero —
 * unrealized P&L) can be immediately re-marked to market instead of flattening
 * the table until the next tick lands. A verified MNQZ6 quote must not
 * overwrite a remembered MNQU6 mark. Entries expire: replaying a stale mark
 * is worse than showing the server truth.
 */
const lastQuotesByRoot = new Map<string, { quote: Record<string, any>; at: number }>();
// Same 10s bound as backend MARK_MAX_AGE_SECONDS (RFC-004 T2 / T3).
const QUOTE_MARK_TTL_MS = MARK_MAX_AGE_SECONDS * 1000;
let lastMarketQueryClient: { setQueriesData: (...args: any[]) => unknown } | null = null;
let markExpiryTimer: ReturnType<typeof setInterval> | null = null;

const payloadAgeMs = (quote: Record<string, any>, now: number): number | null => {
  const raw = quote?.receive_timestamp ?? quote?.timestamp;
  if (typeof raw !== "string") return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? now - parsed : null;
};

const rememberedQuoteKey = (symbol: unknown, contractSymbol: unknown): string | null => {
  const dated = String(contractSymbol ?? "").trim();
  if (dated) return dated;
  return quoteRootFor(symbol) ?? (typeof symbol === "string" && symbol ? symbol : null);
};

const rememberQuote = (quotePayload: Record<string, any>): void => {
  if (typeof quotePayload?.symbol !== 'string') return;
  const key = rememberedQuoteKey(quotePayload.symbol, quotePayload.contract_symbol);
  if (!key) return;
  lastQuotesByRoot.set(key, { quote: quotePayload, at: Date.now() });
};

/**
 * Re-mark cached positions from the most recent live quotes. Called after a
 * positions truth refetch: the REST payload carries the broker's stored
 * unrealized P&L (often 0 right after a fill), which would otherwise stomp
 * the live mark until the next tick. Does NOT record a quote pulse — this is
 * a replay, not fresh tape.
 */
export const reapplyLastQuoteMarks = (queryClient: any): void => {
  void queryClient;
};

/**
 * Handles market data messages from the WebSocket
 * Primarily updates position PnL calculations based on new market data
 */
const positionRoot = (position: Position): string | null =>
  quoteRootFor(
    position.instrument ??
      position.contract_symbol ??
      position.broker_contract?.contract_symbol ??
      position.broker_metadata?.symbol,
  );

/** Perfection P2 (2026-08-18): a row painted "closing" (flatten/liquidation
 * in flight) is STILL open exposure at the broker until a closed frame or
 * refetch proves otherwise — its mark keeps ticking and it keeps counting in
 * open-P&L sums. Freezing it froze the table mid-wave and dropped the group
 * card to a stale "+$0" while 15 accounts still held positions. */
const isLiveExposure = (status: unknown): boolean =>
  status === "active" || status === "closing";

const blankStalePositions = (
  positions: Position[],
  expired: Map<string, number>,
): Position[] => {
  let changed = false;
  const next = positions.map((position) => {
    if (!isLiveExposure(position.status)) return position;
    const dated = String(position.contract_symbol ?? "").trim();
    const root = positionRoot(position);
    // Dated memory expires on its own contract key. An unrelated expiry
    // (MNQZ6 vs MNQU6) must not keep this row fresh or blank it. Fall back
    // to the product-root key only when this expiry has no dated memory.
    let ageSeconds: number | null = null;
    if (dated && expired.has(dated)) {
      ageSeconds = expired.get(dated) ?? null;
    } else if (dated && lastQuotesByRoot.has(dated)) {
      return position;
    } else if (root && expired.has(root)) {
      ageSeconds = expired.get(root) ?? null;
    } else {
      return position;
    }
    // A19: an anchored row does NOT blank when the tape dies — it falls back
    // to the broker's last MEASURED openPnl (the anchor basis). That is aged
    // broker data, not a tape estimate, so it relabels "broker_last_good"
    // (which also re-arms basis capture on the next live tick).
    if (
      position.mark_source === "broker_anchor_projected" &&
      typeof position.anchor_basis_pnl === "number" &&
      Number.isFinite(position.anchor_basis_pnl)
    ) {
      changed = true;
      return {
        ...position,
        pnl: String(position.anchor_basis_pnl),
        // r2 fix 1: both P&L fields move together on every client write.
        unrealized_pnl: position.anchor_basis_pnl,
        mark_source: "broker_last_good" as const,
        anchor_basis_mid: null,
        mark_stale_seconds: null,
      };
    }
    if (
      position.mark_source === "broker_anchor" ||
      position.mark_source === "broker_quote" ||
      position.mark_source === "broker_last_good"
    ) {
      return position;
    }
    if (
      position.pnl === null &&
      position.unrealized_pnl === null &&
      position.mark_stale_seconds === ageSeconds
    ) {
      return position;
    }
    changed = true;
    return {
      ...position,
      pnl: null,
      // r2 fix 1: a blanked row is blank on BOTH fields — leaving a stale
      // unrealized_pnl let the group card show a number the table honestly
      // rendered as "—".
      unrealized_pnl: null,
      mark_stale_seconds: ageSeconds,
    };
  });
  return changed ? next : positions;
};

const totalKnownUnrealized = (positions: Position[]): number =>
  positions.reduce((total, position) => {
    if (!isLiveExposure(position.status)) return total;
    const reading = readPnl(position.pnl);
    return reading.known ? total + reading.value : total;
  }, 0);

const patchChangedQueries = (
  queryClient: Pick<QueryClient, "getQueryCache" | "setQueryData">,
  filters: QueryFilters,
  updater: (oldData: unknown) => unknown,
): void => {
  const queries = queryClient.getQueryCache().findAll(filters);
  for (const query of queries) {
    const oldData = query.state.data;
    const nextData = updater(oldData);
    if (nextData !== oldData) {
      queryClient.setQueryData(query.queryKey, nextData);
    }
  }
};

/**
 * SIG-1199: once a remembered quote ages past 10s, stop painting its last
 * P&L as a live loss. Rows become unknown + `mark_stale_seconds`.
 */
export const expireStaleQuoteMarks = (
  queryClient: { setQueriesData: (...args: any[]) => unknown } | null,
  now: number = Date.now(),
): string[] => {
  if (!queryClient) return [];
  const expired = new Map<string, number>();
  for (const [root, entry] of lastQuotesByRoot) {
    const payloadAge = payloadAgeMs(entry.quote, now);
    const ageMs = payloadAge ?? now - entry.at;
    if (ageMs > QUOTE_MARK_TTL_MS) {
      expired.set(root, Math.floor(ageMs / 1000));
      lastQuotesByRoot.delete(root);
    }
  }
  if (expired.size === 0) return [];

  queryClient.setQueriesData(
    { queryKey: ["positions"] },
    (oldData: any) => {
      const container = getPositionListContainer(oldData);
      if (!container) return oldData;
      const next = blankStalePositions(container.positions, expired);
      if (next === container.positions) return oldData;
      return container.update(next, totalKnownUnrealized(next));
    },
  );
  queryClient.setQueriesData(
    { queryKey: ["profile-positions"] },
    (oldData: Position[]) => {
      if (!oldData) return oldData;
      const next = blankStalePositions(oldData, expired);
      return next === oldData ? oldData : next;
    },
  );
  return [...expired.keys()];
};

export const resetQuoteMarksForTests = (): void => {
  lastQuotesByRoot.clear();
  lastMarketQueryClient = null;
  if (markExpiryTimer !== null) {
    clearInterval(markExpiryTimer);
    markExpiryTimer = null;
  }
};

const ensureMarkExpiryTicker = (
  queryClient: { setQueriesData: (...args: any[]) => unknown },
): void => {
  lastMarketQueryClient = queryClient;
  if (typeof window === "undefined" || markExpiryTimer !== null) return;
  markExpiryTimer = setInterval(() => {
    expireStaleQuoteMarks(lastMarketQueryClient);
  }, 1000);
};

export const handleMarketDataMessage = (message: any, queryClient: any) => {
  if (!message || !queryClient) return;
  ensureMarkExpiryTicker(queryClient);
  expireStaleQuoteMarks(queryClient);

  let quotePayload =
    message.quotes ||
    message.payload?.quotes ||
    message.data?.quotes ||
    (
      (message.type === 'market_data.quote' || message.channel === 'market_data') &&
      (message.payload || message.data)
    ) ||
    null;

  // WS-B2: the live Databento pipe (redis_subscriber "market_data.quote")
  // delivers ONE flat normalized quote per message: {symbol, bid, ask, price}.
  // findRelevantQuote speaks that flat shape natively, but the PnL calc reads
  // `offer` (not `ask`) — map it (falling back to last price for trades-only
  // schemas) so live Databento ticks actually price positions.
  if (
    quotePayload &&
    typeof quotePayload.symbol === 'string' &&
    quotePayload.offer == null &&
    (quotePayload.ask != null || quotePayload.price != null)
  ) {
    quotePayload = {
      ...quotePayload,
      bid: quotePayload.bid ?? quotePayload.price,
      offer: quotePayload.ask ?? quotePayload.price,
    };
  }

  // We're only interested in quote-style updates for now.
  if (quotePayload) {
    const ageMs = payloadAgeMs(quotePayload, Date.now());
    if (ageMs !== null && ageMs > QUOTE_MARK_TTL_MS) {
      return;
    }
    recordQuotePulse();
    rememberQuote(quotePayload);
    void queryClient;
  }
};

/**
 * A19 — anchor-relative projection. A row the broker has priced (openPnl
 * anchor) never recomputes P&L from our recorded entry: the tape only MOVES
 * the broker's measured value by (mid − mid_at_anchor) × multiplier × qty.
 * At every anchor the number IS broker truth, so the row converges to
 * Tradovate instead of drifting on entry/fee differences (the uniform
 * inflated "feed est." estimates the operator measured on 2026-08-17).
 *
 * Returns the next row (identity-equal when nothing changed), or null when
 * this row has no usable anchor — the caller falls through to the labeled
 * from-scratch tape estimate.
 */
const projectFromBrokerAnchor = (
  position: Position,
  relevantQuote: Record<string, any>,
): Position | null => {
  const resolvedPrice = getAppropriatePrice(relevantQuote, position.direction ?? "");
  if (resolvedPrice === null) return null;
  const mid = Number(resolvedPrice);
  const multiplier = getContractMultiplier(position);
  const qty = Number(position.volume ?? position.open_volume);
  const direction = String(position.direction ?? "").toLowerCase();
  const dirSign = direction === "long" ? 1 : direction === "short" ? -1 : 0;
  if (multiplier === null || !Number.isFinite(qty) || qty <= 0 || dirSign === 0) {
    return null;
  }

  // (Re-)capture: a row the broker just priced starts a new basis — this is
  // also how a fresh broker frame (mark_source "broker_anchor" via
  // apply-trading-events) resets a stale basis. r3 fix 4: an anchor frame
  // that carried its OWN reference price already seeded the basis
  // (anchor_basis_pnl/mid on the row) — use it and project THIS tick, so the
  // move between stamp time and this quote is never dropped. Only a
  // basis-less anchor (REST rows, legacy frames) captures from the current
  // tick's mid.
  if (
    position.mark_source === "broker_anchor" ||
    position.mark_source === "broker_last_good"
  ) {
    const reading = readPnl(position.pnl);
    if (!reading.known) return null;
    const seededPnl = position.anchor_basis_pnl;
    const seededMid = position.anchor_basis_mid;
    const hasSeededBasis =
      ["broker_anchor", "broker_last_good"].includes(position.mark_source) &&
      typeof seededPnl === "number" &&
      Number.isFinite(seededPnl) &&
      typeof seededMid === "number" &&
      Number.isFinite(seededMid);
    if (hasSeededBasis) {
      const projected = parseFloat(
        (seededPnl + (mid - seededMid) * multiplier * qty * dirSign).toFixed(2),
      );
      if (Number.isFinite(projected)) {
        return {
          ...position,
          pnl: projected.toString(),
          unrealized_pnl: projected,
          pnl_percentage: calculatePnlPercentage(projected, position),
          mark_source: "broker_anchor_projected" as const,
          mark_stale_seconds: null,
          current_price: resolvedPrice,
        };
      }
    }
    if (position.mark_source === "broker_last_good") {
      const quotedContract = String(relevantQuote.contract_symbol ?? "").trim();
      const positionContract = String(position.contract_symbol ?? position.instrument ?? "").trim();
      return quotedContract && quotedContract === positionContract ? null : position;
    }
    return {
      ...position,
      anchor_basis_pnl: reading.value,
      anchor_basis_mid: mid,
      // r2 fix 1: pnl and unrealized_pnl move TOGETHER in every client write
      // — the group card prefers unrealized_pnl while the table/cards read
      // pnl, and a one-field write forked the surfaces between anchors.
      unrealized_pnl: reading.value,
      mark_source: "broker_anchor_projected" as const,
      mark_stale_seconds: null,
      current_price: resolvedPrice,
    };
  }

  if (
    position.mark_source === "broker_anchor_projected" &&
    typeof position.anchor_basis_pnl === "number" &&
    Number.isFinite(position.anchor_basis_pnl) &&
    typeof position.anchor_basis_mid === "number" &&
    Number.isFinite(position.anchor_basis_mid)
  ) {
    const projected = parseFloat(
      (
        position.anchor_basis_pnl +
        (mid - position.anchor_basis_mid) * multiplier * qty * dirSign
      ).toFixed(2),
    );
    if (!Number.isFinite(projected)) return null;
    const nextPnl = projected.toString();
    const nextPnlPercentage = calculatePnlPercentage(projected, position);
    if (
      position.pnl === nextPnl &&
      // r3 fix 3: the short-circuit must compare BOTH fields — comparing
      // pnl alone preserved an existing pnl/unrealized_pnl mismatch forever.
      position.unrealized_pnl === projected &&
      position.pnl_percentage === nextPnlPercentage &&
      (position.mark_stale_seconds ?? null) === null &&
      position.current_price === resolvedPrice
    ) {
      return position;
    }
    return {
      ...position,
      pnl: nextPnl,
      pnl_percentage: nextPnlPercentage,
      // r2 fix 1: keep both P&L fields in lockstep (see capture branch).
      unrealized_pnl: projected,
      current_price: resolvedPrice,
      mark_stale_seconds: null,
    };
  }

  return null;
};

const getPositionListContainer = (oldData: any) => {
  const hasItems = Array.isArray(oldData?.items);
  const hasResultsPositions = Array.isArray(oldData?.results?.positions);

  if (hasItems || hasResultsPositions) {
    return {
      positions: hasItems ? oldData.items : oldData.results.positions,
      update: (positions: Position[], totalUnrealizedPnL: number) => ({
        ...oldData,
        ...(hasItems ? { items: positions } : {}),
        ...(hasResultsPositions
          ? {
              results: {
                ...oldData.results,
                positions,
                summary: {
                  ...oldData.results.summary,
                  total_unrealized_pnl: totalUnrealizedPnL,
                },
              },
            }
          : {}),
      }),
    };
  }

  if (Array.isArray(oldData?.positions)) {
    return {
      positions: oldData.positions,
      update: (positions: Position[], totalUnrealizedPnL: number) => ({
        ...oldData,
        positions,
        summary: {
          ...oldData.summary,
          total_unrealized_pnl: totalUnrealizedPnL,
        },
      }),
    };
  }

  return null;
};

/**
 * Updates position PnL values using the latest quote data
 */
const updatePositionsPnL = (
  quoteData: Record<string, any>,
  queryClient: any,
  options: { recordPulse?: boolean } = {},
) => {
  if (!quoteData) {
    return
  };
  // A real quote frame proves live tape — the market chip's observed-override
  // reads this pulse, never REST-refetch dataUpdatedAt (SIG-378). A REPLAY of
  // a remembered quote (reapplyLastQuoteMarks) must not fake a pulse.
  if (options.recordPulse !== false) {
    recordQuotePulse();
  }
  //console.log('[WebSocketDebug] Handling market data quotes:', quoteData);
  // Update open positions in the query cache
  patchChangedQueries(
    queryClient,
    {
      queryKey: ["positions"],
      type: "active",
      predicate: (query) => query.queryKey[1] !== "history",
    },
    (oldData) => {
      const container = getPositionListContainer(oldData);
      if (!container) {
        return oldData;
      }

      const oldPositions = container.positions;

      let changed = false;
      const updatedPositions = oldPositions.map((position: Position) => {
        const next = markPositionFromQuote(position, quoteData, 'position');
        if (next !== position) changed = true;
        return next;
      });

      if (!changed) return oldData;

      // Calculate new unrealized PnL total
      const totalUnrealizedPnL = updatedPositions.reduce(
        (total: number, position: Position) => {
          // status is the canonical liveness flag — broker_metadata.netPos is
          // null on the standard serializer path, which silently zeroed this
          // total for every position (SIG-376).
          //
          // SIG-650: read via readPnl so this agrees with the rows and with
          // positions.service. A truthy `position.pnl` test dropped an UNKNOWN
          // P&L silently (pnl: null) while the initial-load aggregator absorbed
          // the same value as a zero — the same total behaving two different
          // wrong ways depending on whether a socket tick had landed yet.
          // Unknowns are excluded here too; the count is surfaced by the
          // service-layer summary rather than recomputed per tick.
          if (!isLiveExposure(position.status)) return total;
          const reading = readPnl(position.pnl);
          return reading.known ? total + reading.value : total;
        },
        0
      );

      // Return updated data with new positions and summary
      return container.update(updatedPositions, totalUnrealizedPnL);
    }
  );

  // Similarly update profile positions if needed
  patchChangedQueries(
    queryClient,
    {
      queryKey: ["profile-positions"],
      type: "active",
    },
    //(queryKey: unknown[]) => Array.isArray(queryKey) && queryKey[0] === 'positions',
    (oldData) => {
      if (!Array.isArray(oldData)) {
        return oldData;
      }

      const oldPositions: Position[] = oldData;

      let changed = false;
      const updatedPositions = oldPositions.map((position: Position) => {
        const next = markPositionFromQuote(position, quoteData, 'profile position');
        if (next !== position) changed = true;
        return next;
      });

      if (!changed) return oldData;

      // SIG-650 follow-up (Greptile): there is deliberately NO total computed
      // here. This cache (`profile-positions`) holds a PLAIN Position[] — it
      // has no summary object to write one into, unlike the `positions` cache
      // above, whose container exposes `update(positions, total)`. A total was
      // being reduced here and then discarded: dead since it was written, and
      // actively misleading, because a reader sees P&L aggregation and assumes
      // a total is maintained for this key. Verified before removing: nothing
      // reads a summary off `profile-positions`.
      //
      // If this cache ever gains a summary, add the total back through the
      // shared readPnl() reader so it agrees with positions.service and the
      // rows — do not reintroduce a truthy `position.pnl` test, which drops an
      // unknown P&L silently.
      return updatedPositions;
    }
  );
};

const getPositionQuoteIdentity = (position: Position) => {
  const metadataContractId =
    position.broker_metadata?.contractId ??
    position.broker_metadata?.contract_id;
  const contractIdValue =
    position.broker_contract?.contract_id ?? metadataContractId;
  const contractId = String(contractIdValue ?? '').trim() || undefined;
  const symbolCandidates = [
    position.instrument,
    position.contract_symbol,
    position.broker_contract?.contract_symbol,
    position.broker_instrument?.broker_symbol,
    position.broker_instrument?.instrument?.symbol,
    position.broker_metadata?.symbol,
    position.broker_metadata?.contract_symbol,
  ]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean);

  return { contractId, symbolCandidates };
};

/**
 * Find the relevant quote for a contract ID
 */
const findRelevantQuote = (
  quoteData: any,
  contractId?: string,
  symbolCandidates: string[] = [],
) => {
  const findInRecord = (record: any) => {
    if (!record || typeof record !== 'object' || Array.isArray(record)) return null;
    const datedCandidates = symbolCandidates.filter((symbol) => symbol !== quoteRootFor(symbol));
    const acceptsDatedIdentity = (quote: any): boolean => {
      if (!quote || typeof quote !== 'object' || Array.isArray(quote)) return false;
      const quotedContract = String(quote.contract_symbol ?? '').trim();
      if (!quotedContract || datedCandidates.length === 0) return true;
      return datedCandidates.includes(quotedContract);
    };
    const accept = (quote: any) => (acceptsDatedIdentity(quote) ? quote : null);

    if (contractId && record[String(contractId)]) {
      const nested = accept(record[String(contractId)]);
      if (nested) return nested;
    }

    for (const symbol of symbolCandidates) {
      if (record[symbol]) {
        const nested = accept(record[symbol]);
        if (nested) return nested;
      }
      if (
        record.symbol === symbol ||
        record.contract_symbol === symbol ||
        record.instrument === symbol
      ) {
        const matched = accept(record);
        if (matched) return matched;
      }
    }

    const quoteRoot = quoteRootFor(record.symbol ?? record.contract_symbol ?? record.instrument);
    if (
      quoteRoot &&
      symbolCandidates.some((s) => quoteRootFor(s) === quoteRoot)
    ) {
      const matched = accept(record);
      if (matched) return matched;
    }

    // Legacy batches can be keyed by the subscribed parent root while the
    // child quote omits symbol: {NQ: {bid, offer}}.
    for (const [key, value] of Object.entries(record)) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
      const child = value as Record<string, unknown>;
      const keyRoot = quoteRootFor(key);
      const childRoot = quoteRootFor(child.symbol ?? child.contract_symbol ?? child.instrument);
      const matches = symbolCandidates.some((candidate) => {
        const candidateRoot = quoteRootFor(candidate);
        return candidateRoot != null && (candidateRoot === keyRoot || candidateRoot === childRoot);
      });
      if (matches) {
        const nested = accept(value);
        if (nested) return nested;
      }
    }

    return null;
  };

  let relevantQuote = null;
  if (Array.isArray(quoteData)) {
    for (const item of quoteData) {
      relevantQuote = findInRecord(item);
      if (relevantQuote) break;
    }
  } else {
    relevantQuote = findInRecord(quoteData);
  }

  if (relevantQuote) return relevantQuote;

  const missKey = contractId || symbolCandidates.join('|');
  if (missKey && !quoteMissesLoggedThisSession.has(missKey)) {
    quoteMissesLoggedThisSession.add(missKey);
    console.debug('[WebSocketDebug] No relevant quote found for contract', {
      contractId,
      symbolCandidates,
    });
  }

  return null;
};

/**
 * Calculate PnL percentage based on position data
 */
const calculatePnlPercentage = (pnl: number, position: any) => {
  if (!position || !position.open_price || !pnl) return '0.00';

  const openPrice = parseFloat(position.open_price);
  const volume = parseFloat(position.volume);

  if (openPrice <= 0 || volume <= 0) return '0.00';

  // Basic percentage calculation (PnL / initial position value * 100)
  const initialValue = openPrice * volume;
  const percentage = (pnl / initialValue) * 100;

  return percentage.toFixed(2);
};

/**
 * A quote as it reaches this module. Every field is optional because the shape
 * varies by source: the live Databento pipe sends a flat {symbol, bid, ask,
 * price}, batched payloads arrive keyed by contract/symbol, and trades-only
 * schemas carry `price` with no book at all.
 */
interface IncomingQuote {
  bid?: number | string | null;
  offer?: number | string | null;
  ask?: number | string | null;
  price?: number | string | null;
  last?: number | string | null;
}

// Feed sentinels ("no value" encoded as int64-max nanos) arrive as
// 9,223,372,036.85… — finite, so isFinite alone let one price a 3-lot MNQ at
/**
 * Mark ONE live-exposure position from a quote frame. Pure: returns the same
 * object when nothing changed, a new row otherwise. Shared by the `positions`
 * and `profile-positions` cache loops — the same fix used to be hand-applied
 * to both (r2 fix 1, r3 fix 3, DL-55 codex r3 f5); one body cannot drift.
 */
export const markPositionFromQuote = (
  position: Position,
  quoteData: Record<string, any>,
  logLabel: string = 'position',
): Position => {
  // Only update LIVE-EXPOSURE positions (active + closing).
  if (!isLiveExposure(position.status)) {
    return position;
  }

  // Check if this position's contract is in the quote data
  const { contractId, symbolCandidates } = getPositionQuoteIdentity(position);

  if (!contractId) {
    debugService.log('info', `No contract ID found for ${logLabel}, falling back to symbol match`, {
      positionId: position.id,
      symbolCandidates,
    });
  }

  // Look for matching quote in the data
  const relevantQuote = findRelevantQuote(quoteData, contractId, symbolCandidates);
  if (!relevantQuote) {
    return position;
  }

  // A19: broker-anchored rows project from the anchor — the tape moves
  // the broker's measured value, it never replaces it. Fall through to
  // the labeled from-scratch estimate only when no anchor exists.
  const anchoredNext = projectFromBrokerAnchor(position, relevantQuote);
  if (anchoredNext !== null) {
    return anchoredNext;
  }

  // Calculate updated PnL
  const quoteKey = contractId || symbolCandidates[0] || 'quote';
  const updatedPnL = calculatePositionPnL(position, {
    type: 'quote_update',
    data: [{ [quoteKey]: relevantQuote }],
  });

  // CRASH FIX follow-through: only overwrite current_price when we actually
  // resolved one. getAppropriatePrice used to return the string '0' for an
  // unusable quote, which marks a live position at zero — a wrong price, not a
  // missing one. Keeping the prior price is the honest fallback. Same rule for
  // the P&L itself: a null calc is a MISS, not a flat position — keep the
  // previous mark instead of stamping "0".
  const resolvedPrice = getAppropriatePrice(relevantQuote, position.direction);
  if (updatedPnL === null) {
    if (resolvedPrice === null || position.current_price === resolvedPrice) {
      return position;
    }
    return { ...position, current_price: resolvedPrice };
  }
  const nextPnl = updatedPnL.toString();
  const nextPnlPercentage = calculatePnlPercentage(updatedPnL, position);
  if (
    position.pnl === nextPnl &&
    // r3 fix 3: compare BOTH P&L fields or an existing mismatch survives the
    // fast path forever.
    position.unrealized_pnl === updatedPnL &&
    position.pnl_percentage === nextPnlPercentage &&
    (position.mark_stale_seconds ?? null) === null &&
    // DL-55 codex r3 f5: an equal value from the tape must still LABEL the
    // row — a broker-labeled row recomputed by the tape may not keep its
    // broker label on the fast path.
    position.mark_source === "tape_degraded" &&
    (resolvedPrice === null || position.current_price === resolvedPrice)
  ) {
    return position;
  }
  return {
    ...position,
    pnl: nextPnl,
    // r2 fix 1: pnl and unrealized_pnl move together in every client write —
    // one-field writes forked the three P&L surfaces.
    unrealized_pnl: updatedPnL,
    pnl_percentage: nextPnlPercentage,
    mark_stale_seconds: null,
    // DL-55: this number came from the Databento tape — label it so the UI
    // can render the degraded-mark badge (broker frames overwrite).
    mark_source: "tape_degraded" as const,
    ...(resolvedPrice !== null ? { current_price: resolvedPrice } : {}),
  };
};

// +$55.3B unrealized (2026-08-12). No instrument here trades above eight
// figures, and no tradable price is <= 0; outside the band = not a price.
const MAX_REASONABLE_PRICE = 10_000_000;

/** Coerce a quote price field to a usable number, or null. Never throws. */
const toFiniteNumber = (value: unknown): number | null => {
  let parsed: number;
  if (typeof value === 'number') {
    parsed = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    parsed = Number(trimmed);
  } else {
    return null;
  }
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed > MAX_REASONABLE_PRICE) return null;
  return parsed;
};

/**
 * Resolve the price to mark a position at, from the quote, by direction.
 *
 * CRASH FIX: this previously did `quote.bid.toString()` / `quote.offer
 * .toString()` behind only a `!quote` guard. That is safe for the FLAT live
 * payload, because handleMarketDataMessage normalises `ask`->`offer` for it —
 * but that normalisation is gated on `typeof quotePayload.symbol === 'string'`,
 * so a NESTED/batched payload (findRelevantQuote returning record[contractId])
 * never gets normalised and arrives with `ask` and no `offer`. A short position
 * then hit `undefined.toString()` and threw INSIDE the setQueriesData updater,
 * on the live market-update path. Batched payloads are far more likely under
 * real tick load than in demo, so this would have surfaced at the open and
 * taken the positions view down with it.
 *
 * Returns null rather than a fabricated price when nothing usable is present —
 * the caller then leaves the existing price alone. The old code returned the
 * string '0', which is not a missing price, it is a WRONG one: it would mark a
 * live position at zero and feed that straight into the P&L.
 */
const getAppropriatePrice = (
  quote: IncomingQuote | null | undefined,
  direction: string,
): string | null => {
  if (!quote) return null;

  // P2 stage 1 (2026-08-12): mark at the MID of the book when both sides are
  // present — the backend's one mark implementation prices at (bid+ask)/2
  // (position_marks mid contract), and marking long-at-bid/short-at-offer
  // left every open position a spread-width away from both the backend and
  // the broker, permanently. Direction-side candidates remain as fallback
  // for one-sided or trades-only payloads.
  const bidSide = toFiniteNumber(quote.bid);
  const offerSide = toFiniteNumber(quote.offer ?? quote.ask);
  if (bidSide !== null && offerSide !== null) {
    return ((bidSide + offerSide) / 2).toString();
  }

  const known = readDirection(direction);
  const candidates =
    known === "long"
      ? [quote.bid, quote.price, quote.last]
      : known === "short"
        ? [quote.offer, quote.ask, quote.price, quote.last]
        : [quote.price, quote.last];

  for (const candidate of candidates) {
    const resolved = toFiniteNumber(candidate);
    if (resolved !== null) return resolved.toString();
  }
  return null;
};
